import React, { useState } from "react";
import {
  PenLine,
  Heading,
  Newspaper,
  FileEdit,
  Globe,
  Bot,
  Table,
  BarChart3,
  Volume2,
  Podcast,
  Image,
  LayoutDashboard,
  ChevronDown,
  AlertCircle,
  HelpCircle,
  Sparkles,
  Palette,
  Scissors,
  Lightbulb,
} from "lucide-react";

const textTools = [
  {
    id: "proofread",
    icon: PenLine,
    name: "Proofread",
    description: "Spelling, grammar & expression check",
    useCase: "When you need a quick grammar check before deadline",
    howTo: [
      "Paste the article body into the input field",
      "Select subtype: Business or General News",
      "Click the Proofread button",
      "Review the list of suggested corrections",
    ],
    subTypes: ["Business — numbers, units & financial terms", "General News — quote accuracy & passive voice improvement"],
    tip: "For long articles, checking paragraph by paragraph gives more accurate results. Proper nouns and technical terms should be verified separately.",
  },
  {
    id: "title",
    icon: Heading,
    name: "Headline",
    description: "7 headline styles based on article content",
    useCase: "When you've finished writing but can't think of a good headline",
    howTo: [
      "Paste the article body or key content into the input field",
      "Select subtype: 5 Types or Creative 7",
      "Click the Headline button",
      "Choose from the suggested headlines or request more",
    ],
    subTypes: ["5 Types — journalism-first, balanced hook, click-bait, SEO, social media", "Creative 7 — language innovation, collision, emotional amplification engine"],
    tip: "Pasting just 2–3 key paragraphs is enough — you don't need the full article.",
  },
  {
    id: "press",
    icon: Newspaper,
    name: "Press Release",
    description: "Convert press releases into article draft format",
    useCase: "When you need to quickly draft an article from a press release",
    howTo: [
      "Paste the full press release into the input field",
      "Select subtype: Government/Public or Corporate",
      "Click the Press Release button",
      "Review the generated draft and add additional reporting",
    ],
    subTypes: ["Government/Public — economic policy impact, real estate & finance regulation", "Corporate — new product highlights, quarterly earnings analysis"],
    tip: "Always verify facts and do additional reporting before using the generated draft.",
    warning: true,
  },
  {
    id: "column",
    icon: FileEdit,
    name: "Revise",
    description: "Improve article structure, flow, and expression",
    useCase: "When your article feels like something is missing",
    howTo: [
      "Paste the completed article into the input field",
      "Select subtype: Structure Analysis or Quick Edit",
      "Click the Revise button",
      "Review AI suggestions and selectively apply them",
    ],
    subTypes: ["Structure Analysis — predict reader drop-off points, hero's journey / mystery / flashback options", "Quick Edit — first-2-second impact, 3 improvement directions to pick from instantly"],
    tip: "Proofread (spelling) and Revise (overall quality) are different features. Use Proofread when pressed for time, Revise when you have more time.",
  },
  {
    id: "foreign",
    icon: Globe,
    name: "Foreign News",
    description: "Translate & summarize foreign news",
    useCase: "When you need to quickly understand or write up a foreign news story",
    howTo: [
      "Paste the original foreign text into the input field",
      "Select language: English or Japanese",
      "Click the Foreign News button",
      "Choose output: full translation / key summary / article draft",
    ],
    subTypes: ["English — CNBC, Reuters, Bloomberg real-time translation", "Japanese — Nikkei, Kyodo News with localized terminology"],
    tip: "Paste the article text directly — not a URL.",
  },
  {
    id: "buddy",
    icon: Bot,
    name: "Buddy",
    description: "AI writing partner for journalists",
    useCase: "For any request not covered by the other tools",
    howTo: [
      "Type your question or request freely in the input field",
      "Select subtype: Brief Buddy or Article Buddy",
      "Ask follow-up questions in a conversational style",
    ],
    subTypes: ["Brief Buddy — enter one word, get 3 article directions", "Article Buddy — input a brief and facts to get a full draft with [brackets] marking items to verify"],
    examples: [
      '"What should I write today? Give me ideas"',
      '"Draft a story brief from this press release"',
      '"Write an article draft from my notes"',
    ],
    tip: "The more specific your request, the better the results.",
  },
  {
    id: "prism",
    icon: Lightbulb,
    name: "PRISM",
    description: "Curate articles by reader type",
    useCase: "When you want to surface the most relevant articles for a specific audience",
    howTo: [
      "Select the reader type you are targeting",
      "Paste an article list or RSS feed",
      "AI selects the best articles for each type",
    ],
    subTypes: ["Students & Job Seekers, Early-career Professionals, Startups, Global Investors", "Corporate CEOs, Financial Products, Real Estate, Stock Investors"],
    tip: "You can select multiple reader types at once.",
    status: "coming",
  },
];

const visualTools = [
  {
    id: "table",
    icon: Table,
    name: "Table",
    description: "Organize data into a clean table",
    useCase: "When you need to quickly organize comparison data or information into a table",
    howTo: [
      "Enter the data or information to turn into a table",
      "Click the Table button",
      "(Optional) Specify table format: comparison, schedule, info box",
      "Review and download the generated table",
    ],
    tip: "Separating data with commas or line breaks improves recognition accuracy.",
  },
  {
    id: "chart_convert",
    icon: Palette,
    name: "Chart Convert",
    description: "Change colors & erase regions from chart images",
    useCase: "When you need to recolor a chart to match your publication's style, or remove watermarks and logos",
    howTo: [
      "Upload a chart image (click or drag)",
      "Color Change mode: click the eyedropper, pick a color from the image, then set the replacement color",
      "(Optional) Use Exclude Region to protect areas from color replacement",
      "Erase Region mode: drag to select logos or watermarks — AI will inpaint them out",
      "Download or copy the result image",
    ],
    tip: "You can chain both modes: apply color changes first, then switch to Erase Region to clean up the result.",
  },
  {
    id: "chart_slice",
    icon: Scissors,
    name: "Chart Slice",
    description: "Extract a specific time range from a chart image",
    useCase: "When you want to highlight a specific period from a long chart without recreating it from scratch",
    howTo: [
      "Upload a chart image (click or drag)",
      "Click Extract Data — AI reads the chart and reconstructs the data",
      "Drag the range handles to select the period you want",
      "Toggle Full Axis ON to keep the original x-axis frame with only the selected range filled",
      "Edit the chart title or series name inline by clicking on them",
      "Download the sliced chart as PNG",
    ],
    tip: "Full Axis OFF gives a clean zoomed-in view. Full Axis ON is useful when you want to show where the slice sits within the full timeline.",
    status: "coming",
  },
  {
    id: "chart",
    icon: BarChart3,
    name: "Chart",
    description: "Generate bar, line, or pie charts from numerical data",
    useCase: "When you need to visualize statistics or numbers as a graph",
    howTo: [
      "Enter numerical data",
      "Click the Chart button",
      "Select chart type: bar / line / pie / combo",
      "Review and download the generated chart (PNG / SVG)",
    ],
    tip: 'Specifying units (trillion, %, count, etc.) improves accuracy. Stating your intent like "emphasize growth" or "highlight comparison" helps recommend the right chart.',
    status: "coming",
  },
  {
    id: "infographic",
    icon: LayoutDashboard,
    name: "Infographic",
    description: "Generate infographic images from information",
    useCase: "When creating SNS posts or card news",
    status: "coming",
  },
  {
    id: "image",
    icon: Image,
    name: "Image",
    description: "Generate thumbnails and illustrations for articles",
    useCase: "When creating a featured image for an article",
    status: "coming",
  },
];

const mediaTools = [
  {
    id: "tts",
    icon: Volume2,
    name: "TTS",
    description: "Convert article text to an audio file",
    useCase: "When producing audio news content",
    status: "coming",
    howTo: [
      "Enter the text to convert to speech",
      "Click the TTS button",
      "Select voice settings (male/female/neutral, speed)",
      "Preview and download the generated audio",
    ],
    tip: 'Spelling out numbers and abbreviations makes the audio sound more natural. e.g., "GDP" → "Gross Domestic Product"',
  },
  {
    id: "podcast",
    icon: Podcast,
    name: "Podcast",
    description: "Convert articles into podcast scripts and audio",
    useCase: "When producing podcast content",
    status: "coming",
    howTo: [
      "Enter one or more article bodies",
      "Click the Podcast button",
      "Select format: news briefing (1–2 min) / analysis (3–5 min) / dialogue (5 min+)",
      "Review the script → generate audio",
    ],
  },
  {
    id: "infographic",
    icon: LayoutDashboard,
    name: "Infographic",
    description: "Generate infographic images from information",
    useCase: "When creating SNS posts or card news",
    status: "coming",
  },
  {
    id: "image",
    icon: Image,
    name: "Image",
    description: "Generate thumbnails and illustrations for articles",
    useCase: "When creating a featured image for an article",
    status: "coming",
  },
];

const faqs = [
  {
    q: "How is this different from other services?",
    a: "14 features — including 6 previously scattered across different sites, plus visualization and multimedia tools — are now integrated into one platform.",
  },
  {
    q: "How is this different from the Claude web version?",
    a: "It runs on the Claude API, so there are no token limits or server downtime issues.",
  },
  {
    q: "Is my input data shared externally?",
    a: "The service operates via API and input data is not used for any purpose other than improving service quality. Please avoid entering sensitive unpublished source information.",
  },
  {
    q: "What should I do if I encounter an error?",
    a: "Please contact the person in charge with a screenshot of the issue.",
  },
];

const ToolCard = ({ tool, expanded, onToggle }) => {
  const Icon = tool.icon;
  const isComingSoon = tool.status === "coming";

  return (
    <div className={`bg-bg-200/50 rounded-xl border border-bg-300/30 overflow-hidden ${isComingSoon ? "opacity-60" : ""}`}>
      <button
        onClick={onToggle}
        disabled={isComingSoon}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-bg-300/30 transition-colors disabled:cursor-not-allowed"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isComingSoon ? "bg-bg-300/50" : "bg-sedaily-500/10"}`}>
          <Icon className={`w-5 h-5 ${isComingSoon ? "text-text-500" : "text-sedaily-500"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-text-100">{tool.name}</h3>
            {isComingSoon && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">Coming Soon</span>
            )}
          </div>
          <p className="text-sm text-text-400 truncate">{tool.description}</p>
        </div>
        {!isComingSoon && (
          <ChevronDown className={`w-4 h-4 text-text-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        )}
      </button>

      {expanded && !isComingSoon && (
        <div className="px-4 pb-4 space-y-4">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-sedaily-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-xs text-text-500">When to use</span>
              <p className="text-sm text-text-200">{tool.useCase}</p>
            </div>
          </div>

          {tool.howTo && (
            <div>
              <span className="text-xs text-text-500 mb-2 block">How to use</span>
              <ol className="space-y-1">
                {tool.howTo.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-300">
                    <span className="text-sedaily-500 font-medium">{idx + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {tool.subTypes && (
            <div>
              <span className="text-xs text-text-500 mb-2 block">Subtypes</span>
              <ul className="space-y-1">
                {tool.subTypes.map((sub, idx) => (
                  <li key={idx} className="text-sm text-text-300 pl-2 border-l-2 border-sedaily-500/30">{sub}</li>
                ))}
              </ul>
            </div>
          )}

          {tool.examples && (
            <div>
              <span className="text-xs text-text-500 mb-2 block">Examples</span>
              <ul className="space-y-1">
                {tool.examples.map((ex, idx) => (
                  <li key={idx} className="text-sm text-text-300 pl-2 border-l-2 border-bg-300">{ex}</li>
                ))}
              </ul>
            </div>
          )}

          {tool.tip && (
            <div className={`text-sm p-3 rounded-lg ${tool.warning ? "bg-red-500/10 text-red-400" : "bg-sedaily-500/10 text-sedaily-400"}`}>
              {tool.warning ? "Warning: " : "Tip: "}
              {tool.tip}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, tools, icon: SectionIcon }) => {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <SectionIcon className="w-5 h-5 text-sedaily-500" />
        <h2 className="text-lg font-semibold text-text-100">{title}</h2>
        <span className="text-sm text-text-500">({tools.length} tools)</span>
      </div>
      <div className="grid gap-3">
        {tools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            expanded={expandedId === tool.id}
            onToggle={() => setExpandedId(expandedId === tool.id ? null : tool.id)}
          />
        ))}
      </div>
    </div>
  );
};

const FAQItem = ({ faq, expanded, onToggle }) => (
  <div className={`bg-bg-200/50 rounded-xl border border-bg-300/30 overflow-hidden`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 p-4 text-left hover:bg-bg-300/30 transition-colors"
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-sedaily-500/10 flex-shrink-0">
        <HelpCircle className="w-5 h-5 text-sedaily-500" />
      </div>
      <span className="flex-1 text-sm font-medium text-text-100">{faq.q}</span>
      <ChevronDown className={`w-4 h-4 text-text-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded && (
      <div className="px-4 pb-4 pl-[72px]">
        <p className="text-sm text-text-400">{faq.a}</p>
      </div>
    )}
  </div>
);

const ServiceGuide = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="h-full overflow-y-auto bg-bg-100">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-100 mb-2">AI Labs Service Guide</h1>
          <p className="text-text-400">
            Handle repetitive writing tasks faster with AI. 14 tools integrated into one platform.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="bg-bg-200/50 rounded-xl p-4 text-center border border-bg-300/30">
            <div className="text-2xl font-bold text-sedaily-500 mb-1">15</div>
            <div className="text-sm text-text-400">Integrated Tools</div>
          </div>
          <div className="bg-bg-200/50 rounded-xl p-4 text-center border border-bg-300/30">
            <div className="text-2xl font-bold text-sedaily-500 mb-1">1</div>
            <div className="text-sm text-text-400">Single Platform</div>
          </div>
          <div className="bg-bg-200/50 rounded-xl p-4 text-center border border-bg-300/30">
            <div className="text-2xl font-bold text-sedaily-500 mb-1">API</div>
            <div className="text-sm text-text-400">Stable Operation</div>
          </div>
        </div>

        <div className="space-y-10">
          <Section title="Text Tools" tools={textTools} icon={PenLine} />
          <Section title="Visualization Tools" tools={visualTools} icon={BarChart3} />
          <Section title="Multimedia Tools" tools={mediaTools} icon={Volume2} />
        </div>

        <div className="mt-10 p-5 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-400 mb-2">Important Notes</h3>
              <ul className="space-y-2 text-sm text-text-300">
                <li>Always verify facts before using AI-generated content.</li>
                <li>Avoid entering sensitive information such as unpublished source names.</li>
                <li>AI is a support tool — final judgment and responsibility rest with the journalist.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-lg font-semibold text-text-100 mb-4">FAQ</h2>
          <div className="grid gap-3">
            {faqs.map((faq, idx) => (
              <FAQItem
                key={idx}
                faq={faq}
                expanded={expandedFaq === idx}
                onToggle={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
              />
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-bg-300/30 text-center text-sm text-text-500">
          <p>Contact: ai@sedaily.com</p>
          <p className="mt-1">Version 1.0</p>
        </div>
      </div>
    </div>
  );
};

export default ServiceGuide;
